import { useState, useMemo, useRef } from 'react';
import { Link } from 'react-router-dom';
import { createPortal } from 'react-dom';
import { Icon, Popup, Image, Loader } from 'semantic-ui-react';
import SusuModal from '../common/SusuModal';
import BackgroundCustomizer from '../background-customizer/BackgroundCustomizer';
import { useShare } from '../../context/ShareContext';
import AuthButton from '../auth-button/AuthButton';
import PersonImageEditor from '../person-image-editor/PersonImageEditor';
import {
  useGetUserProfileQuery,
  useUpdateProfileMutation,
  useUpdateAvatarMutation,
  useDeleteAccountMutation
} from '../../redux/apis/endpoints/authApi';
import './UnifiedCanvasControls.css';

const UnifiedCanvasControls = ({
  // Navigation handlers - no longer used here but kept prop interface
  onHomeClick,
  onFolderClick,

  // Background customization
  showBackgroundCustomizer,
  setShowBackgroundCustomizer,
  background,
  onBackgroundChange,
  onBackgroundImageUpload,

  // Layout
  availableWidth = 1200,
  isCollapsed = false,
  onToggleCollapse,
}) => {
  const [activeOverlay, setActiveOverlay] = useState(null);

  // Settings modal state
  const [isSettingsOpen, setIsSettingsOpen] = useState(false);
  const [newUsername, setNewUsername] = useState('');
  const [avatarPreview, setAvatarPreview] = useState(null);
  const [selectedFile, setSelectedFile] = useState(null);
  const [isEditorOpen, setIsEditorOpen] = useState(false);
  const [editorImageUrl, setEditorImageUrl] = useState(null);
  const [isSavingCrop, setIsSavingCrop] = useState(false);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [deleteConfirmText, setDeleteConfirmText] = useState('');
  const fileInputRef = useRef(null);

  // RTK Query hooks - only fetch profile when logged in
  const isLoggedIn = !!localStorage.getItem('token');
  const { data: user } = useGetUserProfileQuery(undefined, { skip: !isLoggedIn });
  const [updateProfile, { isLoading: isUpdatingProfile }] = useUpdateProfileMutation();
  const [updateAvatar, { isLoading: isUpdatingAvatar }] = useUpdateAvatarMutation();
  const [deleteAccount, { isLoading: isDeletingAccount }] = useDeleteAccountMutation();
  const { openShare } = useShare();

  // Privacy is a single-switch setting: flip it and it saves, no Save button.
  const handleTogglePrivate = async () => {
    try {
      await updateProfile({ isPrivate: !user?.isPrivate }).unwrap();
    } catch (error) {
      console.error('Failed to update account privacy:', error);
    }
  };

  const handleShareProfile = () => {
    setIsSettingsOpen(false);
    openShare({
      sourceType: 'profile',
      title: user?.username,
      previewImages: [user?.profilePic?.url || user?.profilePicUrl].filter(Boolean)
    });
  };


  // Calculate layout based on available width
  const layout = useMemo(() => {
    const controlSpacing = 16;
    const iconSize = 40;
    const modeWidth = 200;
    const buttonWidth = 140;

    const totalControlsWidth = (iconSize * 5) + modeWidth + (buttonWidth * 2) + (controlSpacing * 8);
    const canFitSingleRow = availableWidth >= totalControlsWidth;

    return {
      canFitSingleRow,
      controlSpacing,
      iconSize,
      modeWidth,
      buttonWidth
    };
  }, [availableWidth]);

  const handleOverlayToggle = (overlayName) => {
    if (activeOverlay === overlayName) {
      setActiveOverlay(null);
      // Close specific overlays
      if (overlayName === 'background') setShowBackgroundCustomizer(false);
    } else {
      setActiveOverlay(overlayName);
      // Open specific overlays
      if (overlayName === 'background') {
        setShowBackgroundCustomizer(true);
      }
    }
  };

  // Settings Modal Handlers
  const handleOpenSettings = () => {
    setNewUsername(user?.username || '');
    setAvatarPreview(null);
    setSelectedFile(null);
    setDeleteConfirmText('');
    setShowDeleteConfirm(false);
    setIsSettingsOpen(true);
  };

  const handleCloseSettings = () => {
    setIsSettingsOpen(false);
    setAvatarPreview(null);
    setSelectedFile(null);
    setEditorImageUrl(null);
    setDeleteConfirmText('');
    setShowDeleteConfirm(false);
  };

  const handleSaveUsername = async () => {
    try {
      await updateProfile({ username: newUsername }).unwrap();
    } catch (error) {
      console.error('Failed to update username:', error);
    }
  };

  const handleSelectFileClick = () => {
    fileInputRef.current?.click();
  };

  const handleFileChange = (e) => {
    const file = e.target.files?.[0];
    if (file) {
      const objectUrl = URL.createObjectURL(file);
      setEditorImageUrl(objectUrl);
      setIsEditorOpen(true);
      e.target.value = '';
    }
  };

  const handleEditorSave = async (blob) => {
    try {
      setIsSavingCrop(true);
      const file = new File([blob], 'avatar.jpg', { type: 'image/jpeg' });
      setSelectedFile(file);
      const previewUrl = URL.createObjectURL(blob);
      setAvatarPreview(previewUrl);
      setIsEditorOpen(false);
    } catch (error) {
      console.error('Failed to save cropped image:', error);
    } finally {
      setIsSavingCrop(false);
    }
  };

  const handleSaveAvatar = async () => {
    if (!selectedFile) return;
    try {
      await updateAvatar(selectedFile).unwrap();
      setAvatarPreview(null);
      setSelectedFile(null);
      setEditorImageUrl(null);
    } catch (error) {
      console.error('Failed to update avatar:', error);
    }
  };

  const handleDeleteAccount = async () => {
    try {
      await deleteAccount().unwrap();
      localStorage.removeItem('token');
      window.location.href = '/';
    } catch (error) {
      console.error('Failed to delete account:', error);
    }
  };

  if (isCollapsed) {
    return (
      <div className="unified-canvas-controls collapsed">
        <Popup
          trigger={
            <button
              className="expand-controls-btn"
              onClick={onToggleCollapse}
            >
              <Icon name="bars" />
            </button>
          }
          content="Show Controls"
          position="bottom center"
          size="mini"
          inverted
        />
      </div>
    );
  }

  return (
    <div className={`unified-canvas-controls ${layout.canFitSingleRow ? 'single-row' : 'multi-row'}`}>
      {/* Collapse Button - Top Left Corner */}
      <button
        className="control-collapse-btn-corner"
        onClick={onToggleCollapse}
      >
        <Icon name="minus" />
      </button>

      <div className="controls-container">
        {/* First Row - Navigation and Tools */}
        <div className="controls-first-row">
          {user ? (
            /* User Profile Section */
            <div className="control-section user-profile-section">
              {/* Profile Picture */}
              <div className="profile-pic-container">
                {(user.profilePic?.url || user.profilePicUrl) ? (
                  <Image src={user.profilePic?.url || user.profilePicUrl} avatar className="user-avatar-image" />
                ) : (
                  <div className="user-initial-avatar">
                    {user.username ? user.username.charAt(0).toUpperCase() : 'U'}
                  </div>
                )}
              </div>

              {/* Username with Settings Button */}
              <div className="username-container">
                <span className="user-name">{user.username}</span>
                <Icon
                  name="setting"
                  size="small"
                  className="settings-icon"
                  onClick={handleOpenSettings}
                  title="Account Settings"
                />
              </div>

              <AuthButton useDimmer={true} />
            </div>
          ) : (
            /* Guest View */
            <div className="control-section navigation-section">
              {/* Icons deleted as per request */}
            </div>
          )}

          {/* Center Section */}
          <div className="control-section center-section">
            <nav className="canvas-site-nav" aria-label="Site navigation"><Link to="/">Home</Link><a href="/#about">About</a><Link to="/contact">Contact</Link></nav>
          </div>

          {/* Right Section - Auth Button for Guests */}
          {!user && (
            <div className="control-section actions-section">
              <AuthButton />
            </div>
          )}
        </div>
      </div>

      {/* Settings Modal — portaled to body so parent transforms don't break centering */}
      {isSettingsOpen && createPortal(
        <div className="settings-backdrop" onClick={handleCloseSettings}>
          <div className="settings-modal" onClick={(e) => e.stopPropagation()}>

            {/* Loading overlay */}
            {(isUpdatingProfile || isUpdatingAvatar || isDeletingAccount) && (
              <div className="settings-loading-overlay">
                <Loader active size="large" />
              </div>
            )}

            {/* Header */}
            <div className="settings-header">
              <span className="settings-header-title">
                <Icon name="setting" /> Account Settings
              </span>
              <button className="settings-close-btn" onClick={handleCloseSettings}>
                <Icon name="close" />
              </button>
            </div>

            {/* Content */}
            <div className="settings-content">

              {/* Profile Picture Section */}
              <div className="settings-section settings-avatar-section">
                <div className="settings-avatar-preview">
                  {(avatarPreview || user?.profilePic?.url || user?.profilePicUrl) ? (
                    <img
                      src={avatarPreview || user?.profilePic?.url || user?.profilePicUrl}
                      className="settings-avatar-img"
                      alt="Profile"
                    />
                  ) : (
                    <div className="user-initial-avatar large">
                      {user?.username ? user.username.charAt(0).toUpperCase() : 'U'}
                    </div>
                  )}
                </div>
                <div className="settings-avatar-actions">
                  <button className="settings-btn settings-btn-outline" onClick={handleSelectFileClick}>
                    <Icon name="camera" /> Change Photo
                  </button>
                  <input
                    type="file"
                    ref={fileInputRef}
                    hidden
                    accept="image/*"
                    onChange={handleFileChange}
                  />
                  {selectedFile && (
                    <button
                      className="settings-btn settings-btn-teal"
                      onClick={handleSaveAvatar}
                      disabled={isUpdatingAvatar}
                    >
                      Save Photo
                    </button>
                  )}
                </div>
              </div>

              <div className="settings-divider" />

              {/* Username Section */}
              <div className="settings-section">
                <div className="settings-section-label">Username</div>
                <div className="settings-inline-row">
                  <input
                    className="settings-input"
                    placeholder="Username"
                    value={newUsername}
                    onChange={(e) => setNewUsername(e.target.value)}
                  />
                  <button
                    className="settings-btn settings-btn-teal"
                    onClick={handleSaveUsername}
                    disabled={!newUsername || newUsername === user?.username || isUpdatingProfile}
                  >
                    Save
                  </button>
                </div>
              </div>

              <div className="settings-divider" />

              {/* Profile Sharing Section */}
              <div className="settings-section">
                <div className="settings-section-label">Profile</div>

                <button
                  className={`settings-privacy-row ${user?.isPrivate ? 'on' : ''}`}
                  onClick={handleTogglePrivate}
                  disabled={isUpdatingProfile}
                  type="button"
                  role="switch"
                  aria-checked={!!user?.isPrivate}
                >
                  <span className="settings-privacy-copy">
                    <span className="settings-privacy-title">
                      <Icon name={user?.isPrivate ? 'lock' : 'globe'} /> Private account
                    </span>
                    <span className="settings-privacy-desc">
                      {user?.isPrivate
                        ? 'Your profile link shows only your name and photo.'
                        : 'Anyone with your profile link can see your closets and lookbooks.'}
                    </span>
                  </span>
                  <span className="settings-privacy-switch">
                    <span className="settings-privacy-knob" />
                  </span>
                </button>

                <button
                  className="settings-btn settings-btn-outline settings-share-profile"
                  onClick={handleShareProfile}
                  type="button"
                >
                  <Icon name="share alternate" /> Share my profile
                </button>
              </div>

              <div className="settings-divider" />

              {/* Delete Account Section */}
              <div className="settings-section settings-danger-zone">
                <div className="settings-section-label danger">Delete Account</div>
                <p className="settings-danger-text">
                  Permanently deletes your account, closet items, looks, and listings. This cannot be undone.
                </p>
                {!showDeleteConfirm ? (
                  <button
                    className="settings-btn settings-btn-danger-outline"
                    onClick={() => setShowDeleteConfirm(true)}
                  >
                    <Icon name="trash" /> Delete My Account
                  </button>
                ) : (
                  <div className="settings-delete-confirm">
                    <p className="settings-confirm-prompt">
                      Type <strong>DELETE</strong> to confirm:
                    </p>
                    <div className="settings-inline-row">
                      <input
                        className="settings-input"
                        placeholder="Type DELETE"
                        value={deleteConfirmText}
                        onChange={(e) => setDeleteConfirmText(e.target.value)}
                      />
                      <button
                        className="settings-btn settings-btn-danger"
                        onClick={handleDeleteAccount}
                        disabled={deleteConfirmText !== 'DELETE' || isDeletingAccount}
                      >
                        Confirm
                      </button>
                      <button
                        className="settings-btn settings-btn-outline"
                        onClick={() => { setShowDeleteConfirm(false); setDeleteConfirmText(''); }}
                      >
                        Cancel
                      </button>
                    </div>
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>,
        document.body
      )}

      {/* Image Editor Modal */}
      <SusuModal
        open={isEditorOpen}
        onClose={() => !isSavingCrop && setIsEditorOpen(false)}
        size="small"
        className="image-editor-modal"
      >
        <SusuModal.Header>
          <Icon name="crop" />
          Edit Profile Picture
        </SusuModal.Header>
        <SusuModal.Content>
          {editorImageUrl && (
            <PersonImageEditor
              imageUrl={editorImageUrl}
              onSave={handleEditorSave}
              onCancel={() => setIsEditorOpen(false)}
              isSaving={isSavingCrop}
            />
          )}
        </SusuModal.Content>
      </SusuModal>

      {/* Overlay Panels */}
      {showBackgroundCustomizer && (
        <div className="control-overlay background-overlay">
          <div className="overlay-content">
            <BackgroundCustomizer
              currentBackground={background}
              onBackgroundChange={onBackgroundChange}
              onImageUpload={onBackgroundImageUpload}
            />
            <button
              className="close-overlay-btn"
              onClick={() => handleOverlayToggle('background')}
            >
              <Icon name="close" />
            </button>
          </div>
        </div>
      )}
    </div>
  );
};

export default UnifiedCanvasControls;